/**
 * @fileoverview Milestone Set Templates Management Hook
 * 
 * Provides functionality for managing reusable milestone set templates that can be
 * applied to jobs. Milestone sets are collections of predefined milestones with
 * standard deliverables, timelines, and responsibilities that can be saved and
 * reused across multiple jobs.
 * 
 * **Milestone Set Templates:**
 * Organizations can create custom milestone sets tailored to their workflows:
 * - Standard templates (admin-only): Available to all organizations
 * - Organization templates: Private to specific organization
 * - Categorized by project type (manufacturing, design, development, etc.)
 * - Reusable across multiple jobs for consistency
 * 
 * **Template Structure:**
 * Each milestone set contains:
 * - Metadata: Name, category, description
 * - Template items: Ordered list of milestone definitions
 * - Usage tracking: Count of times template has been applied
 * - Ownership: Creator and organization information
 * 
 * **Milestone Template Items:**
 * Individual milestones within a set include:
 * - Milestone name and description
 * - Sequence order for correct timeline placement
 * - Milestone type (planning, design, development, review, etc.)
 * - Deliverable description
 * - Action owner (client or vendor responsibility)
 * 
 * **Common Use Cases:**
 * - Apply standard milestone timeline to new jobs
 * - Create organization-specific workflows
 * - Ensure consistency across similar projects
 * - Speed up job setup with pre-configured timelines
 * - Share best practices across teams
 * 
 * **Lifecycle:**
 * 1. Create: Define milestone set with template items
 * 2. Save: Store in organization's template library
 * 3. Browse: Filter and search available templates
 * 4. Apply: Create job milestones from template
 * 5. Track: Monitor usage across jobs
 * 6. Update: Modify template (doesn't affect existing jobs)
 * 7. Clone: Create variations of existing templates
 * 8. Delete: Soft delete (preserves usage history)
 * 
 * @module useMilestoneSets
 * 
 * @example
 * ```tsx
 * // Browse and apply milestone templates
 * function MilestoneTemplateSelector({ jobId }) {
 *   const {
 *     milestoneSets,
 *     loading,
 *     incrementUsageCount
 *   } = useMilestoneSets({ category: 'manufacturing' });
 * 
 *   const applyTemplate = async (templateId: string) => {
 *     const template = milestoneSets.find(t => t.id === templateId);
 *     if (template) {
 *       // Create milestones from template items
 *       await createMilestonesFromTemplate(jobId, template);
 *       await incrementUsageCount(templateId);
 *     }
 *   };
 * 
 *   return (
 *     <div>
 *       {milestoneSets.map(set => (
 *         <TemplateCard
 *           key={set.id}
 *           template={set}
 *           onApply={() => applyTemplate(set.id)}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Create custom milestone set
 * function CreateMilestoneSetForm() {
 *   const { createMilestoneSet } = useMilestoneSets();
 * 
 *   const handleCreate = async (formData) => {
 *     const milestones = [
 *       {
 *         milestone_name: 'Initial Design',
 *         sequence_order: 1,
 *         milestone_type: 'design',
 *         deliverable: 'Preliminary design mockups',
 *         action_owner: 'vendor'
 *       },
 *       {
 *         milestone_name: 'Design Review',
 *         sequence_order: 2,
 *         milestone_type: 'client_approval',
 *         deliverable: 'Approved design',
 *         action_owner: 'client'
 *       }
 *     ];
 * 
 *     await createMilestoneSet(
 *       'Standard Design Project',
 *       milestones,
 *       'design',
 *       'Standard timeline for design projects'
 *     );
 *   };
 * 
 *   return <MilestoneSetForm onSubmit={handleCreate} />;
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Clone and modify existing template
 * function CloneTemplateDialog({ templateId }) {
 *   const { cloneMilestoneSet } = useMilestoneSets();
 * 
 *   const handleClone = async (newName: string) => {
 *     await cloneMilestoneSet(templateId, newName);
 *     toast({ title: 'Template cloned successfully' });
 *   };
 * 
 *   return (
 *     <Dialog>
 *       <Input
 *         placeholder="New template name"
 *         onChange={(e) => setNewName(e.target.value)}
 *       />
 *       <Button onClick={() => handleClone(newName)}>
 *         Clone Template
 *       </Button>
 *     </Dialog>
 *   );
 * }
 * ```
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/lib/auth';

/**
 * Individual milestone within a milestone set template.
 * 
 * Defines a single milestone that will be created when the template is applied
 * to a job. Includes all necessary information to create a fully configured
 * milestone with proper sequencing and responsibilities.
 */
export interface MilestoneTemplateItem {
  id?: string;
  milestone_set_id?: string;
  milestone_name: string;
  sequence_order: number;
  description?: string;
  milestone_type: 'planning' | 'design' | 'development' | 'review' | 'testing' | 'delivery' | 'client_approval' | 'installation';
  deliverable: string;
  action_owner: 'client' | 'vendor';
}

/**
 * Milestone set template with associated milestone items.
 * 
 * Represents a complete milestone template that can be applied to jobs.
 * Includes metadata about the template and an array of milestone definitions.
 */
export interface MilestoneSet {
  id: string;
  organization_id: string;
  name: string;
  category?: string;
  description?: string;
  created_by_user_id: string;
  usage_count: number;
  created_at: string;
  updated_at: string;
  milestone_template_items?: MilestoneTemplateItem[];
}

/**
 * Options for filtering milestone sets.
 */
interface UseMilestoneSetsOptions {
  /** Filter by category (e.g., 'manufacturing', 'design') */
  category?: string;
  /** Search by template name */
  search?: string;
}

/**
 * Hook for managing milestone set templates
 * 
 * Provides complete CRUD operations for milestone set templates including
 * creation, updating, cloning, deletion, and usage tracking. Templates can be
 * filtered by category and searched by name.
 * 
 * Features:
 * - Fetch templates with filtering options
 * - Create new milestone set templates
 * - Update existing templates (admins can modify standard flag)
 * - Clone templates for variations
 * - Soft delete templates (preserves usage history)
 * - Track template usage across jobs
 * - Admin-only standard templates
 * - Organization-scoped templates
 * 
 * Standard Templates:
 * Only admins can create or modify standard templates (is_standard = true).
 * Standard templates are available to all organizations and serve as
 * best-practice examples for common project types.
 * 
 * Template Items:
 * Milestone items are automatically sorted by sequence_order for consistent
 * display. Items can be reordered by updating their sequence_order values.
 * 
 * @param options - Filtering options for milestone sets
 * @returns Object with milestone sets, loading state, and management functions
 * 
 * @example
 * ```typescript
 * // Fetch manufacturing templates
 * const {
 *   milestoneSets,
 *   loading,
 *   createMilestoneSet,
 *   updateMilestoneSet
 * } = useMilestoneSets({ category: 'manufacturing' });
 * 
 * // Create new template
 * await createMilestoneSet(
 *   'Custom Manufacturing',
 *   milestoneItems,
 *   'manufacturing',
 *   'Our standard manufacturing workflow'
 * );
 * ```
 */
export function useMilestoneSets(options: UseMilestoneSetsOptions = {}) {
  const [milestoneSets, setMilestoneSets] = useState<MilestoneSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const fetchMilestoneSets = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('milestone_set_templates')
        .select(`
          *,
          milestone_template_items(*)
        `)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (options.category) {
        query = query.eq('category', options.category);
      }

      if (options.search) {
        query = query.ilike('name', `%${options.search}%`);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Sort milestone items by sequence_order
      const setsWithSortedItems = (data || []).map(set => ({
        ...set,
        milestone_template_items: (set.milestone_template_items || []).sort(
          (a: any, b: any) => a.sequence_order - b.sequence_order
        ) as MilestoneTemplateItem[]
      }));

      setMilestoneSets(setsWithSortedItems as MilestoneSet[]);
    } catch (err) {
      console.error('Error fetching milestone sets:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMilestoneSets();
  }, [options.category, options.search]);

  const createMilestoneSet = async (
    name: string,
    milestones: MilestoneTemplateItem[],
    category?: string,
    description?: string,
    isStandard?: boolean
  ) => {
    try {
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id, role')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      // Only admins can create standard milestone sets
      const shouldBeStandard = isStandard && profile.role === 'admin';

      // Create the milestone set
      const { data: milestoneSet, error: setError } = await supabase
        .from('milestone_set_templates')
        .insert({
          name,
          category,
          description,
          is_standard: shouldBeStandard,
          organization_id: profile.organization_id,
          created_by_user_id: user.id,
        })
        .select()
        .single();

      if (setError) throw setError;

      // Create milestone items
      const items = milestones.map((milestone, index) => ({
        milestone_set_id: milestoneSet.id,
        milestone_name: milestone.milestone_name,
        sequence_order: milestone.sequence_order || index + 1,
        description: milestone.description,
        milestone_type: milestone.milestone_type,
        deliverable: milestone.deliverable,
        action_owner: milestone.action_owner,
      }));

      const { error: itemsError } = await supabase
        .from('milestone_template_items')
        .insert(items);

      if (itemsError) throw itemsError;

      toast({
        title: 'Milestone set created',
        description: `${name} has been saved to your library${shouldBeStandard ? ' as a standard template' : ''}`,
      });

      fetchMilestoneSets();
      return milestoneSet;
    } catch (err) {
      console.error('Error creating milestone set:', err);
      toast({
        title: 'Error creating milestone set',
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const updateMilestoneSet = async (
    id: string,
    name: string,
    milestones: MilestoneTemplateItem[],
    category?: string,
    description?: string,
    isStandard?: boolean
  ) => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', user?.id || '')
        .single();

      // Build update object - only admins can modify is_standard
      const updateData: any = { name, category, description };
      if (profile?.role === 'admin' && isStandard !== undefined) {
        updateData.is_standard = isStandard;
      }

      // Update the milestone set
      const { error: setError } = await supabase
        .from('milestone_set_templates')
        .update(updateData)
        .eq('id', id);

      if (setError) throw setError;

      // Delete existing items
      const { error: deleteError } = await supabase
        .from('milestone_template_items')
        .delete()
        .eq('milestone_set_id', id);

      if (deleteError) throw deleteError;

      // Create new items
      const items = milestones.map((milestone, index) => ({
        milestone_set_id: id,
        milestone_name: milestone.milestone_name,
        sequence_order: milestone.sequence_order || index + 1,
        description: milestone.description,
        milestone_type: milestone.milestone_type,
        deliverable: milestone.deliverable,
        action_owner: milestone.action_owner,
      }));

      const { error: itemsError } = await supabase
        .from('milestone_template_items')
        .insert(items);

      if (itemsError) throw itemsError;

      toast({
        title: 'Milestone set updated',
        description: `${name} has been updated`,
      });

      fetchMilestoneSets();
    } catch (err) {
      console.error('Error updating milestone set:', err);
      toast({
        title: 'Error updating milestone set',
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const cloneMilestoneSet = async (id: string, newName: string) => {
    try {
      const setToClone = milestoneSets.find(s => s.id === id);
      if (!setToClone) throw new Error('Milestone set not found');

      await createMilestoneSet(
        newName,
        setToClone.milestone_template_items || [],
        setToClone.category,
        setToClone.description
      );
    } catch (err) {
      console.error('Error cloning milestone set:', err);
      toast({
        title: 'Error cloning milestone set',
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const deleteMilestoneSet = async (id: string) => {
    try {
      // Soft delete
      const { error } = await supabase
        .from('milestone_set_templates')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Milestone set deleted',
        description: 'The milestone set has been removed from your library',
      });

      fetchMilestoneSets();
    } catch (err) {
      console.error('Error deleting milestone set:', err);
      toast({
        title: 'Error deleting milestone set',
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const incrementUsageCount = async (id: string) => {
    try {
      const set = milestoneSets.find(s => s.id === id);
      if (set) {
        await supabase
          .from('milestone_set_templates')
          .update({ usage_count: set.usage_count + 1 })
          .eq('id', id);
      }
    } catch (err) {
      console.error('Error incrementing usage count:', err);
    }
  };

  return {
    milestoneSets,
    loading,
    error,
    refetch: fetchMilestoneSets,
    createMilestoneSet,
    updateMilestoneSet,
    cloneMilestoneSet,
    deleteMilestoneSet,
    incrementUsageCount,
  };
}
